// WHAT TO DO ABOUT THIS BLOCKER, in plain language (#6679 slice A).
//
// A blocker can leave a person stuck. Four producers attach CHOICES today, and the rest
// open a words-only ask against a question like "the release workflow reads NPM_TOKEN and
// no secret of that name exists" — which is not a judgement with options, it is work only
// a person can do. "No suggestions" is not a state a blocker may end in.
//
// So a blocker may also carry GUIDANCE: numbered steps, written for THIS blocker from the
// run's own evidence by the account's triage-route model. Not a help page, and not a
// choice: the person follows the steps, then answers in words, which files an ask a judge
// reads exactly as any other answer does.
//
// WHY A DISCRIMINATED SHAPE AND ONE COLUMN. "We tried and there is nothing worth showing"
// has to be distinguishable from "we have not tried yet", because the generator gets ONE
// attempt per blocker and no retry (a bound that names the failure it prevents, never a
// bill: `docs/standards/agent-budgets.md`). With `none` as a member, `guidance IS NOT
// NULL` is the whole of that bound. A second `guidance_attempted_at` column would be the
// same fact spelled twice, and the one that drifts.
//
// DIFFERENT FROM `options`, deliberately. `options` is written once at insert and a guard
// enforces it (`blockers-options-immutable.test.ts`), because a picked choice is resolved
// against the row OUTSIDE the answer transaction. Nothing resolves guidance — it is read
// and rendered, never matched — so it may be written after the blocker opens, which is
// what lets a sweep generate it at all.

import { z } from 'zod';

/** A step is one instruction. Long enough for a path and a reason, short enough to read. */
export const BLOCKER_STEP_MAX = 400;
/** Two is not a list and seven is a document; the prompt asks for three to six. */
export const BLOCKER_STEPS_MAX = 8;
/** The action label and the free-text field's label: both are buttons' worth of words. */
export const BLOCKER_GUIDANCE_LABEL_MAX = 60;

/** Why a blocker ended up with no steps. Each is a thing that happened, never a guess. */
export const BlockerGuidanceNoneReason = {
  /** The model judged that nothing here is a step a person could follow. */
  NoMeaningfulSteps: 'no_meaningful_steps',
  /** It answered, and the adjudicator refused every step it offered. */
  Invalid: 'invalid',
  /**
   * The call was REACHED and faulted — a provider error, a refused output, a deadline.
   *
   * Not "there was no key to call with": a pool that is walled or empty means the attempt
   * never happened, and stamping it here would spend the one attempt on a call nobody
   * made and leave every blocker opened during a five-hour key wall permanently
   * stepless. The generator writes NOTHING in that case, so the row stays `guidance IS
   * NULL` and the next pass re-reads it for the price of a SELECT.
   *
   * A reached fault IS written, and the bound it buys names its failure: the queue is
   * oldest-first and bounded, so a row that faults every pass at the head of it starves
   * every row behind it.
   */
  Unavailable: 'unavailable',
} as const;
export const BlockerGuidanceNoneReasonSchema = z.enum(BlockerGuidanceNoneReason);
export type BlockerGuidanceNoneReason = z.infer<typeof BlockerGuidanceNoneReasonSchema>;

/**
 * One step, as stored. A STRING and not a structure: it is prose a person reads, and the
 * moment it becomes `{action, target}` something starts rendering it as a widget.
 *
 * THE EM-DASH RULE IS NOT HERE, and that is a boundary decision rather than an omission.
 * It applies to this text as to any other string a person reads, and with more force than
 * to hand-written copy: the em dash is the tell of generated copy (#1407, #7087) and this
 * text is generated. But its one verdict is `emDashLines` in `@developerz/i18n`, and this
 * package imports nothing internal — `scripts/import-boundaries.test.ts` pins that row
 * empty. Re-spelling the character here would be the rule in two places with nothing
 * holding them together, so the ONE producer of these steps applies the real verdict
 * before handing them over: the guidance sweep in `apps/worker/src/cron/blocker-guidance.ts`,
 * which depends on `@developerz/i18n` already. {@link blockerGuidanceFromModel} takes the
 * steps as handed over and judges only what it can judge from here: shape and length.
 */
export const BlockerGuidanceStepSchema = z.string().min(1).max(BLOCKER_STEP_MAX);

export const BlockerGuidanceSchema = z.discriminatedUnion('kind', [
  z.object({
    v: z.literal(1),
    kind: z.literal('steps'),
    steps: z.array(BlockerGuidanceStepSchema).min(1).max(BLOCKER_STEPS_MAX),
    /**
     * What the free-text box asks for, when the steps end in a value to bring back ("Paste
     * the DSN"). Absent means the box keeps its ordinary label: the person reports what
     * they did, or what they found instead.
     */
    free_label: z.string().min(1).max(BLOCKER_GUIDANCE_LABEL_MAX).optional(),
    /** The model that wrote the steps, as the picker spells it, for the provenance line. */
    model: z.string().min(1).max(200),
    generated_at: z.string().min(1),
  }),
  z.object({
    v: z.literal(1),
    kind: z.literal('none'),
    reason: BlockerGuidanceNoneReasonSchema,
    generated_at: z.string().min(1),
  }),
]);
export type BlockerGuidance = z.infer<typeof BlockerGuidanceSchema>;

/**
 * Model output into storable guidance, or `none` with the reason.
 *
 * ADJUDICATED HERE, after the parse, for the same reason `blockerOptionsFromLabels` is:
 * the steps are a model's, so one bad step must cost the STEPS and never the blocker.
 * Elements come in as `unknown` so a non-string cannot reject the whole batch.
 *
 * A step this refuses is DROPPED rather than rewritten: rewriting model prose here would
 * be this function guessing at a sentence it cannot read. If dropping leaves nothing the
 * answer is `invalid`, which is a RECORDED outcome and not a silent empty list — the
 * difference that makes the one-attempt bound honest.
 */
export function blockerGuidanceFromModel(input: {
  readonly steps: readonly unknown[] | undefined;
  readonly freeLabel?: unknown;
  readonly model: string;
  readonly at: Date;
}): BlockerGuidance {
  const at = input.at.toISOString();
  if (input.steps === undefined || input.steps.length === 0) {
    return { v: 1, kind: 'none', reason: 'no_meaningful_steps', generated_at: at };
  }
  const steps = input.steps.flatMap((step) => {
    const parsed = BlockerGuidanceStepSchema.safeParse(
      typeof step === 'string' ? step.trim() : step,
    );
    return parsed.success ? [parsed.data] : [];
  });
  if (steps.length === 0) return { v: 1, kind: 'none', reason: 'invalid', generated_at: at };
  const label = z.string().min(1).max(BLOCKER_GUIDANCE_LABEL_MAX).safeParse(input.freeLabel);
  return {
    v: 1,
    kind: 'steps',
    steps: steps.slice(0, BLOCKER_STEPS_MAX),
    ...(label.success ? { free_label: label.data } : {}),
    model: input.model,
    generated_at: at,
  };
}

/**
 * Is this blocker one the generator should write guidance for?
 *
 * TRUE for an OPEN blocker with no guidance yet whose answer is work rather than a
 * judgement with options — so a row that already offers choices is left alone, and a row
 * that has been tried once is never tried again (`guidance` non-null IS the attempt).
 *
 * Takes the three fields and not the row, so the sweep's SQL and this verdict cannot
 * disagree about what "awaiting guidance" means.
 */
export function blockerAwaitsGuidance(blocker: {
  readonly resolution: string | null;
  readonly options: unknown;
  readonly guidance: unknown;
}): boolean {
  return blocker.resolution === null && blocker.options === null && blocker.guidance === null;
}
